import ContentWrapper from "components/content-wrapper/ContentWrapper";
import ActuatorConfiguration from "components/gateways/actuators/ActuatorConfiguration";
import ActuatorInfo from "components/gateways/actuators/ActuatorInfo";
import RelatedEvents from "components/gateways/actuators/RelatedEvents";
import { HttpStatus, LDSUTabPanel } from "constant";
import { ActuatorConfigurationContext } from "context/ActuatorConfigurationContext";
import { get, isEmpty, isEqual } from "lodash";
import { useEffect, useState } from "react";
import { Breadcrumb, Col, Container, Row } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import { fetchDetailDevice, getActuatorCommand } from "service/gatewayService";
import { getActuatorConfigTemplate } from "utils/actuatorFunctions";
import {
    getPrimaryUnit,
    getPrimaryUnitName,
    isFourWiredMotorControllerApp,
    isHttpSuccess,
    clsToAttributeTable,
} from "utils/functions";

const { FORBIDDEN } = HttpStatus;

const ActuatorDetails = () => {
    const {
        gatewayId,
        ldsuId,
        said,
        busNumber,
    }: { gatewayId: string; ldsuId: string; said: string; busNumber: string } =
        useParams();
    const location: any = useLocation();
    const [initState, setInitState] = useState<any>(location.state || {});
    const { type, gatewayName } = initState;
    const [ldsuName, setLdsuName] = useState("");
    const [actuator, setActuator]: any = useState({});
    const [editedActuator, setEditedActuator]: any = useState({});
    const [editMode, setEditMode] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [isIOControllerState, setIsIOControllerState]: any = useState({
        isIOController: false,
        isAnalog: false,
        unit: "",
        minmax: [],
    });
    const [isFourWired, setIsFourWired] = useState(false);
    const [guardTimesConfig, setGuardTimesConfig] = useState<string[] | null>(null);

    useEffect(() => {
        const editedActuatorConfig = getActuatorConfigTemplate(editedActuator);
        const actuatorConfig = getActuatorConfigTemplate(actuator);

        setEditMode(!isEqual(actuatorConfig, editedActuatorConfig));
    }, [editedActuator, actuator]);

    useEffect(() => {
        const handleFetchDevice = async () => {
            const res = await fetchDetailDevice({
                gateway_id: gatewayId,
                device_id: ldsuId,
                said: said,
            });

            if (get(res, "status") === FORBIDDEN) {
                setIsForbiddenResource(true);
                return;
            }

            let _type = type;

            if (!res) {
                return;
            }

            if (isEmpty(initState)) {
                let determinedType = res.type; // fallback to res.type

                if (res.APP?.[said]) {
                    // IO controller - use APP.NAME
                    determinedType = res.APP[said].NAME;
                } else if (res.infoJson?.SNS?.[said]) {
                    // Regular actuator - use CLS mapping
                    const cls = res.infoJson.SNS[said].CLS;
                    if (cls) {
                        determinedType = get(clsToAttributeTable(), cls);
                    }
                }

                setInitState({
                    type: determinedType,
                    gatewayName: res.gateway_name,
                    name: res.NAME,
                });
                _type = determinedType;
            }

            setLdsuName(res.NAME);

            if (!res.APP) {
                return;
            }

            //if ldsu is IO controller

            const {
                CLS: IOControllerCLS,
                MODE,
                APPLICATION,
            } = res.infoJson.SNS[said];

            const defaultUnitObj: any = getPrimaryUnitName(res.APP[said]);

            const unit = getPrimaryUnit(res.APP[said]);

            let mode = MODE && Array.isArray(MODE) && MODE;
            const application = APPLICATION?.find(
                (item: any) => item.APPID === res.APP[said].APPID
            );
            const isFourWiredMotor = isFourWiredMotorControllerApp(res.OBJ, application.APPID);
            setIsFourWired(isFourWiredMotor);

            // Read CONFIG for 4-wire motor (guard times)
            // CONFIG is at device level, not inside APP items
            if (isFourWiredMotor && res.CONFIG) {
                setGuardTimesConfig(res.CONFIG);
            }

            if (application?.MODE && Array.isArray(application?.MODE)) {
                mode = application.MODE;
            }

            // CLS = 32769 is Analog IO controller, otherwise digital IO controller
            if (IOControllerCLS === "32769") {
                setIsIOControllerState({
                    ...isIOControllerState,
                    isIOController: true,
                    isAnalog: true,
                    isGenericAnalogOutput:
                        _type.toLowerCase() === "generic analog output",
                    unit: unit,
                    minmax: [defaultUnitObj.MIN, defaultUnitObj.MAX],
                    format: defaultUnitObj.FORMAT,
                    accuracy: defaultUnitObj.ACCURACY,
                    appId: res.APP[said].APPID,
                });
            } else {
                setIsIOControllerState({
                    ...isIOControllerState,
                    isIOController: true,
                    isAnalog: false,
                    appId: res.APP[said].APPID,
                    mode: mode,
                });
            }
        };

        handleFetchDevice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewayId, ldsuId, said]);

    useEffect(() => {
        const fetchActuator = async () => {
            const actuatorRes: any = await getActuatorCommand(
                gatewayId,
                ldsuId,
                said
            );

            if (isHttpSuccess(actuatorRes.status)) {
                const { data: actuator } = actuatorRes;
                setActuator(actuator);
                setEditedActuator(actuator);
            }
            setIsPageLoading(false);
        };
        fetchActuator();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewayId, ldsuId, said]);

    return (
        <ContentWrapper
            isLoading={isPageLoading}
            isForbiddenResource={isForbiddenResource}
        >
            <ActuatorConfigurationContext.Provider
                value={{
                    actuator,
                    setActuator,
                    gatewayName,
                    ldsuName,
                    type,
                    editedActuator,
                    setEditedActuator,
                    editMode,
                    isIOControllerState,
                    setEditMode,
                    isFourWired,
                    gatewayId,
                    ldsuId,
                    guardTimesConfig,
                }}
            >
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={12} className="sensor-header">
                                <h5 className="page-title preserve-whitespace">
                                    {actuator.name}
                                </h5>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm="12">
                                <Breadcrumb className="w-100">
                                    <li className="breadcrumb-item">
                                        <Link
                                            to={{
                                                pathname: `/gateway-details/${gatewayId}`,
                                            }}
                                        >
                                            Gateway
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link
                                            to={{
                                                pathname: `/gateway-details/${gatewayId}/LDSBus/${busNumber}`,
                                                state: {
                                                    gatewayName: gatewayName,
                                                    panel: LDSUTabPanel.Actuator,
                                                },
                                            }}
                                        >
                                            All Actuators
                                        </Link>
                                    </li>
                                    <Breadcrumb.Item active>
                                        Actuator
                                    </Breadcrumb.Item>
                                </Breadcrumb>
                            </Col>
                        </Row>
                        <Row className="flex-row-reverse">
                            <ActuatorInfo />

                            <ActuatorConfiguration />
                        </Row>

                        <RelatedEvents />
                    </Container>
                </div>
            </ActuatorConfigurationContext.Provider>
        </ContentWrapper>
    );
};
export default ActuatorDetails;
